import React, { createContext, useContext, useCallback, useEffect, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useStore } from './StoreContext';
import { useToast } from './ToastContext';
import { usePermissions } from '../hooks/usePermissions';
import { useWhatsAppCall, ActiveCallView } from '../hooks/useWhatsAppCall';
import { useTelnyxCall } from '../hooks/useTelnyxCall';
import { useSipCall } from '../hooks/useSipCall';
import centreService from '../services/centre.service';
import centreVoipService from '../services/centreVoip.service';
import CentreCallOverlay from '../pages/Centre/CentreCallOverlay';
import { peekIncomingCallLink } from '../utils/incomingCallLink';

interface WhatsAppCallContextValue {
  view: ActiveCallView | null;
  voipEnabled: boolean;
  startCall: (args: { phone: string; name?: string; orderId?: number | null }) => Promise<void>;
  startPstnCall: (args: { phone: string; name?: string; orderId?: number | null }) => Promise<void>;
}

const WhatsAppCallContext = createContext<WhatsAppCallContextValue | null>(null);

export const useWhatsAppCallContext = () => useContext(WhatsAppCallContext);

export const usePstnHandler = (opts?: { orderId?: number | null; name?: string | null }) => {
  const ctx = useWhatsAppCallContext();
  return (phone: string) => (event: React.MouseEvent) => {
    if (!ctx?.voipEnabled || !phone) return;
    event.preventDefault();
    void ctx.startPstnCall({
      phone,
      name: opts?.name || undefined,
      orderId: opts?.orderId || null
    });
  };
};

export const WhatsAppCallProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentStore, selectStore } = useStore();
  const { accessToken } = useAuth();
  const { hasPermission, loading } = usePermissions();
  const { info, success, error } = useToast();
  const waEnabled = !loading && hasPermission('CENTRE_ACCESS') && hasPermission('CENTRE_WHATSAPP');
  const voipAllowed = !loading && hasPermission('CENTRE_ACCESS') && hasPermission('CENTRE_VOIP');
  const [recordingEnabled, setRecordingEnabled] = useState(false);
  const [voipEnabled, setVoipEnabled] = useState(false);
  const [voipProvider, setVoipProvider] = useState<'telnyx' | 'sip' | null>(null);

  useEffect(() => {
    const link = peekIncomingCallLink();
    if (link?.storeId && currentStore?.id !== link.storeId) {
      selectStore(link.storeId);
    }
  }, [currentStore?.id, selectStore]);

  useEffect(() => {
    if (!waEnabled || !currentStore?.id) {
      setRecordingEnabled(false);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      centreService.getCallRecordingStatus(currentStore.id)
        .then((on) => {
          if (!cancelled) setRecordingEnabled(on);
        })
        .catch(() => {
          if (!cancelled) setRecordingEnabled(false);
        });
    }, 1200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [currentStore?.id, waEnabled]);

  useEffect(() => {
    if (!voipAllowed || !currentStore?.id) {
      setVoipEnabled(false);
      setVoipProvider(null);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
    centreVoipService.getStatus(currentStore.id)
      .then((status) => {
        if (cancelled) return;
        setVoipEnabled(!!status.enabled);
        setVoipProvider(status.provider === 'sip' ? 'sip' : status.enabled ? 'telnyx' : null);
      })
      .catch(() => {
        if (!cancelled) {
          setVoipEnabled(false);
          setVoipProvider(null);
        }
      });
    }, 1200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [currentStore?.id, voipAllowed]);

  const onNeedPermission = useCallback(() => {
    info('Autorisation envoyée');
  }, [info]);

  const onPermissionReply = useCallback((granted: boolean) => {
    if (granted) success('Appels autorisés');
  }, [success]);

  const wa = useWhatsAppCall({
    storeId: currentStore?.id || null,
    accessToken,
    enabled: waEnabled,
    recordingEnabled,
    onNeedPermission,
    onPermissionReply,
    onError: error
  });

  const telnyx = useTelnyxCall({
    storeId: currentStore?.id || null,
    accessToken,
    enabled: voipAllowed && voipEnabled && voipProvider === 'telnyx',
    onError: error
  });

  const sip = useSipCall({
    storeId: currentStore?.id || null,
    enabled: voipAllowed && voipEnabled && voipProvider === 'sip',
    onError: error
  });

  const voip = voipProvider === 'sip' ? sip : telnyx;

  const view = voip.view || wa.view;
  const isVoip = view?.provider === 'voip';

  const startPstnCall = useCallback(async (args: { phone: string; name?: string; orderId?: number | null }) => {
    if (!voipEnabled) {
      error('Téléphonie inactive');
      return;
    }
    if (wa.view || voip.view) {
      error('Appel en cours');
      return;
    }
    await voip.startCall(args);
  }, [error, voip, voipEnabled, wa.view]);

  return (
    <WhatsAppCallContext.Provider value={{ view, voipEnabled, startCall: wa.startCall, startPstnCall }}>
      {children}
      {view && (
        <CentreCallOverlay
          view={view}
          muted={isVoip ? voip.muted : wa.muted}
          onAccept={isVoip ? voip.accept : wa.accept}
          onReject={isVoip ? voip.reject : wa.reject}
          onHangup={isVoip ? voip.hangup : wa.hangup}
          onToggleMute={isVoip ? voip.toggleMute : wa.toggleMute}
          onToggleHold={isVoip ? voip.toggleHold : undefined}
        />
      )}
    </WhatsAppCallContext.Provider>
  );
};
